// Everything about a record that changes with where it is from. A batch that is all Smiths on Main Street is a batch
// that never finds the column too narrow for "Zimmermann", and one that is all ASCII never finds the column that
// cannot hold 佐藤 — which is most of what somebody generating test data is actually looking for.

export interface Locale {
  id: LocaleId;
  label: string;
  givenNames: string[];
  surnames: string[];
  // Surname first is not a formatting preference, it is the name; a page that reordered it would be writing it wrong.
  familyNameFirst?: boolean;
  cities: string[];
  streets: string[];
  // `{number}`, `{street}` and `{block}` in the order this country writes them on an envelope.
  addressFormat: string;
  // `#` is a digit and `?` an upper-case letter; one of these is drawn per address.
  postcodes: string[];
  // What a `?` in a postcode may be, where the country leaves some letters out of its codes.
  postcodeLetters?: string;
  regions: string[];
  regionLabel: string;
  country: string;
  countryCode: string;
  // The regulator's range for fiction where it keeps one, and where it keeps none, digits its plan gives no service.
  phones: string[];
  currency: string;
  // The IBAN country a bank account in this locale is written for, absent where the country has no IBAN at all.
  iban?: string;
  companySuffixes: string[];
}

export type LocaleId = "en-US" | "en-GB" | "de-DE" | "fr-FR" | "es-ES" | "it-IT" | "ja-JP";

export const LOCALES: Record<LocaleId, Locale> = {
  "en-US": {
    id: "en-US",
    label: "United States",
    givenNames: [
      "Emma",
      "Liam",
      "Olivia",
      "Noah",
      "Ava",
      "Ethan",
      "Sophia",
      "Mason",
      "Isabella",
      "Logan",
      "Mia",
      "Lucas",
      "Charlotte",
      "Jackson",
      "Amelia",
      "Aiden",
      "Harper",
      "Elijah",
      "Evelyn",
      "James",
      "Abigail",
      "Benjamin",
      "Emily",
      "Alexander",
      "Elizabeth",
      "Michael",
      "Sofia",
      "Daniel",
      "Avery",
      "Henry",
      "Ella",
      "Sebastian",
    ],
    surnames: [
      "Smith",
      "Johnson",
      "Williams",
      "Brown",
      "Jones",
      "Garcia",
      "Miller",
      "Davis",
      "Rodriguez",
      "Martinez",
      "Hernandez",
      "Lopez",
      "Gonzalez",
      "Wilson",
      "Anderson",
      "Thomas",
      "Taylor",
      "Moore",
      "Jackson",
      "Martin",
      "Lee",
      "Perez",
      "Thompson",
      "White",
      "Harris",
      "Sanchez",
      "Clark",
      "Ramirez",
      "Lewis",
      "Robinson",
      "Walker",
      "Young",
    ],
    cities: [
      "New York",
      "Los Angeles",
      "Chicago",
      "Houston",
      "Phoenix",
      "Philadelphia",
      "San Antonio",
      "San Diego",
      "Dallas",
      "Austin",
      "Seattle",
      "Denver",
      "Boston",
      "Portland",
      "Nashville",
      "Atlanta",
    ],
    streets: [
      "Main Street",
      "Oak Avenue",
      "Maple Drive",
      "Cedar Lane",
      "Elm Street",
      "Pine Road",
      "Washington Avenue",
      "Lakeview Drive",
      "Sunset Boulevard",
      "Park Place",
      "Hillcrest Road",
      "River Road",
      "Willow Way",
      "Birch Court",
      "Franklin Street",
      "Highland Avenue",
    ],
    addressFormat: "{number} {street}",
    postcodes: ["#####", "#####-####"],
    regions: [
      "Alabama",
      "Arizona",
      "California",
      "Colorado",
      "Florida",
      "Georgia",
      "Illinois",
      "Massachusetts",
      "Michigan",
      "New York",
      "North Carolina",
      "Ohio",
      "Oregon",
      "Pennsylvania",
      "Texas",
      "Washington",
    ],
    regionLabel: "State",
    country: "United States",
    countryCode: "US",
    // 555-0100 through 555-0199 is the block the NANPA keeps for fiction, which is the only US number safe to print.
    phones: ["+1 2## 555-01##", "+1 3## 555-01##", "+1 4## 555-01##"],
    currency: "USD",
    companySuffixes: ["Inc.", "LLC", "Corp.", "Holdings", "Group"],
  },
  "en-GB": {
    id: "en-GB",
    label: "United Kingdom",
    givenNames: [
      "Oliver",
      "Amelia",
      "George",
      "Isla",
      "Harry",
      "Ava",
      "Noah",
      "Emily",
      "Jack",
      "Mia",
      "Leo",
      "Sophia",
      "Charlie",
      "Grace",
      "Oscar",
      "Lily",
      "Arthur",
      "Freya",
      "Henry",
      "Ivy",
      "Thomas",
      "Poppy",
      "Alfie",
      "Florence",
      "William",
      "Willow",
      "James",
      "Rosie",
      "Theo",
      "Daisy",
      "Jacob",
      "Phoebe",
    ],
    surnames: [
      "Smith",
      "Jones",
      "Taylor",
      "Brown",
      "Williams",
      "Wilson",
      "Johnson",
      "Davies",
      "Patel",
      "Robinson",
      "Wright",
      "Thompson",
      "Evans",
      "Walker",
      "White",
      "Roberts",
      "Green",
      "Hall",
      "Wood",
      "Jackson",
      "Clarke",
      "Turner",
      "Hughes",
      "Edwards",
      "Hill",
      "Moore",
      "Clark",
      "Harrison",
      "Scott",
      "Young",
      "Morris",
      "Lee",
    ],
    cities: [
      "London",
      "Manchester",
      "Birmingham",
      "Leeds",
      "Glasgow",
      "Liverpool",
      "Bristol",
      "Sheffield",
      "Edinburgh",
      "Cardiff",
      "Nottingham",
      "Newcastle upon Tyne",
      "Brighton",
      "Leicester",
      "Oxford",
      "Cambridge",
    ],
    streets: [
      "High Street",
      "Station Road",
      "Church Lane",
      "Victoria Road",
      "Green Lane",
      "Manor Road",
      "Park Road",
      "Queens Road",
      "Kings Road",
      "New Road",
      "Mill Lane",
      "School Lane",
      "The Avenue",
      "Springfield Road",
      "Alexandra Road",
      "York Road",
    ],
    addressFormat: "{number} {street}",
    // Real districts in all six shapes, since a letter drawn at random makes areas like DK and Q that no post reaches.
    postcodes: [
      "E1 #??",
      "E14 #??",
      "E1W #??",
      "N1 #??",
      "N16 #??",
      "N1C #??",
      "NW3 #??",
      "NW10 #??",
      "SE1 #??",
      "SE15 #??",
      "W2 #??",
      "W12 #??",
      "W1D #??",
      "SW1A #??",
      "SW1P #??",
      "EC1A #??",
      "EC4M #??",
      "WC1B #??",
      "WC2N #??",
      "M1 #??",
      "M14 #??",
      "B1 #??",
      "B15 #??",
      "LS1 #??",
      "LS11 #??",
      "G1 #??",
      "G12 #??",
      "L1 #??",
      "L17 #??",
      "BS1 #??",
      "BS16 #??",
      "S1 #??",
      "S10 #??",
      "EH1 #??",
      "EH10 #??",
      "CF10 #??",
      "CF24 #??",
      "NG1 #??",
      "NE1 #??",
      "NE15 #??",
      "BN1 #??",
      "LE1 #??",
      "LE11 #??",
      "OX1 #??",
      "OX14 #??",
      "CB1 #??",
      "CB22 #??",
    ],
    // Royal Mail keeps C, I, K, M, O and V out of the inward code, each being too easily read as another character.
    postcodeLetters: "ABDEFGHJLNPQRSTUWXYZ",
    regions: [
      "Greater London",
      "Greater Manchester",
      "West Midlands",
      "West Yorkshire",
      "Merseyside",
      "Kent",
      "Essex",
      "Hampshire",
      "Surrey",
      "Lancashire",
      "Devon",
      "Norfolk",
      "Fife",
      "Gwynedd",
      "County Antrim",
      "Somerset",
    ],
    regionLabel: "County",
    // Ofcom keeps 07700 900000-900999, 020 7946 0000-0999 and 0161 496 0000-0999 among its ranges for drama.
    phones: ["+44 7700 900###", "+44 20 7946 0###", "+44 161 496 0###"],
    country: "United Kingdom",
    countryCode: "GB",
    currency: "GBP",
    iban: "GB",
    companySuffixes: ["Ltd", "PLC", "LLP", "Group", "Holdings"],
  },
  "de-DE": {
    id: "de-DE",
    label: "Germany",
    givenNames: [
      "Lukas",
      "Emma",
      "Leon",
      "Mia",
      "Finn",
      "Hannah",
      "Paul",
      "Sophia",
      "Jonas",
      "Emilia",
      "Elias",
      "Lina",
      "Ben",
      "Marie",
      "Noah",
      "Lena",
      "Felix",
      "Anna",
      "Maximilian",
      "Laura",
      "Julian",
      "Johanna",
      "Moritz",
      "Clara",
      "Tim",
      "Charlotte",
      "Niklas",
      "Leonie",
      "David",
      "Sarah",
      "Jan",
      "Nele",
    ],
    surnames: [
      "Müller",
      "Schmidt",
      "Schneider",
      "Fischer",
      "Weber",
      "Meyer",
      "Wagner",
      "Becker",
      "Schulz",
      "Hoffmann",
      "Schäfer",
      "Koch",
      "Bauer",
      "Richter",
      "Klein",
      "Wolf",
      "Schröder",
      "Neumann",
      "Schwarz",
      "Zimmermann",
      "Braun",
      "Krüger",
      "Hofmann",
      "Hartmann",
      "Lange",
      "Schmitt",
      "Werner",
      "Krause",
      "Meier",
      "Lehmann",
      "Schmid",
      "Schulze",
    ],
    cities: [
      "Berlin",
      "Hamburg",
      "München",
      "Köln",
      "Frankfurt am Main",
      "Stuttgart",
      "Düsseldorf",
      "Leipzig",
      "Dortmund",
      "Essen",
      "Bremen",
      "Dresden",
      "Hannover",
      "Nürnberg",
      "Duisburg",
      "Bochum",
    ],
    streets: [
      "Hauptstraße",
      "Schulstraße",
      "Gartenstraße",
      "Bahnhofstraße",
      "Dorfstraße",
      "Bergstraße",
      "Kirchstraße",
      "Lindenstraße",
      "Schillerstraße",
      "Goethestraße",
      "Amselweg",
      "Birkenweg",
      "Ringstraße",
      "Mühlenweg",
      "Feldstraße",
      "Waldstraße",
    ],
    addressFormat: "{street} {number}",
    postcodes: ["#####"],
    regions: [
      "Baden-Württemberg",
      "Bayern",
      "Berlin",
      "Brandenburg",
      "Bremen",
      "Hamburg",
      "Hessen",
      "Mecklenburg-Vorpommern",
      "Niedersachsen",
      "Nordrhein-Westfalen",
      "Rheinland-Pfalz",
      "Saarland",
      "Sachsen",
      "Sachsen-Anhalt",
      "Schleswig-Holstein",
      "Thüringen",
    ],
    regionLabel: "Bundesland",
    country: "Deutschland",
    countryCode: "DE",
    // The Bundesnetzagentur's drama numbers in Berlin, Frankfurt, Hamburg, Köln and München, and two mobile blocks.
    phones: [
      "+49 30 23125 ###",
      "+49 69 90009 ###",
      "+49 40 66969 ###",
      "+49 221 4710 ###",
      "+49 89 99998 ###",
      "+49 171 39200 ##",
      "+49 176 040690 ##",
    ],
    currency: "EUR",
    iban: "DE",
    companySuffixes: ["GmbH", "AG", "GmbH & Co. KG", "SE", "e.K."],
  },
  "fr-FR": {
    id: "fr-FR",
    label: "France",
    givenNames: [
      "Gabriel",
      "Emma",
      "Louis",
      "Jade",
      "Raphaël",
      "Louise",
      "Jules",
      "Alice",
      "Adam",
      "Chloé",
      "Lucas",
      "Lina",
      "Léo",
      "Rose",
      "Hugo",
      "Anna",
      "Arthur",
      "Mila",
      "Nathan",
      "Julia",
      "Paul",
      "Léa",
      "Théo",
      "Manon",
      "Ethan",
      "Camille",
      "Maël",
      "Inès",
      "Noah",
      "Sarah",
      "Tom",
      "Zoé",
    ],
    surnames: [
      "Martin",
      "Bernard",
      "Thomas",
      "Petit",
      "Robert",
      "Richard",
      "Durand",
      "Dubois",
      "Moreau",
      "Laurent",
      "Simon",
      "Michel",
      "Lefebvre",
      "Leroy",
      "Roux",
      "David",
      "Bertrand",
      "Morel",
      "Fournier",
      "Girard",
      "Bonnet",
      "Dupont",
      "Lambert",
      "Fontaine",
      "Rousseau",
      "Vincent",
      "Muller",
      "Lefèvre",
      "Faure",
      "André",
      "Mercier",
      "Blanc",
    ],
    cities: [
      "Paris",
      "Marseille",
      "Lyon",
      "Toulouse",
      "Nice",
      "Nantes",
      "Montpellier",
      "Strasbourg",
      "Bordeaux",
      "Lille",
      "Rennes",
      "Reims",
      "Toulon",
      "Grenoble",
      "Dijon",
      "Angers",
    ],
    streets: [
      "Rue de la Paix",
      "Avenue des Champs",
      "Rue Victor Hugo",
      "Boulevard Saint-Germain",
      "Rue de la République",
      "Place de la Mairie",
      "Rue des Écoles",
      "Avenue de la Gare",
      "Rue du Moulin",
      "Allée des Tilleuls",
      "Rue Jean Jaurès",
      "Chemin des Vignes",
      "Rue de l'Église",
      "Boulevard Voltaire",
      "Rue Pasteur",
      "Impasse des Lilas",
    ],
    addressFormat: "{number} {street}",
    postcodes: ["#####"],
    regions: [
      "Auvergne-Rhône-Alpes",
      "Bourgogne-Franche-Comté",
      "Bretagne",
      "Centre-Val de Loire",
      "Corse",
      "Grand Est",
      "Hauts-de-France",
      "Île-de-France",
      "Normandie",
      "Nouvelle-Aquitaine",
      "Occitanie",
      "Pays de la Loire",
      "Provence-Alpes-Côte d'Azur",
      "Guadeloupe",
      "Martinique",
      "La Réunion",
    ],
    regionLabel: "Région",
    country: "France",
    countryCode: "FR",
    // The six blocks ARCEP's numbering plan allocates to audiovisual works, five geographic and one mobile.
    phones: [
      "+33 1 99 00 ## ##",
      "+33 2 61 91 ## ##",
      "+33 3 53 01 ## ##",
      "+33 4 65 71 ## ##",
      "+33 5 36 49 ## ##",
      "+33 6 39 98 ## ##",
    ],
    currency: "EUR",
    iban: "FR",
    companySuffixes: ["SARL", "SA", "SAS", "SCI", "EURL"],
  },
  "es-ES": {
    id: "es-ES",
    label: "Spain",
    givenNames: [
      "Lucía",
      "Hugo",
      "Sofía",
      "Martín",
      "Martina",
      "Mateo",
      "María",
      "Leo",
      "Paula",
      "Daniel",
      "Julia",
      "Alejandro",
      "Emma",
      "Pablo",
      "Valeria",
      "Manuel",
      "Carla",
      "Álvaro",
      "Daniela",
      "Adrián",
      "Alba",
      "Diego",
      "Noa",
      "Marcos",
      "Vega",
      "Enzo",
      "Carmen",
      "Bruno",
      "Lola",
      "Gonzalo",
      "Elena",
      "Javier",
    ],
    surnames: [
      "García",
      "Rodríguez",
      "González",
      "Fernández",
      "López",
      "Martínez",
      "Sánchez",
      "Pérez",
      "Gómez",
      "Martín",
      "Jiménez",
      "Ruiz",
      "Hernández",
      "Díaz",
      "Moreno",
      "Muñoz",
      "Álvarez",
      "Romero",
      "Alonso",
      "Gutiérrez",
      "Navarro",
      "Torres",
      "Domínguez",
      "Vázquez",
      "Ramos",
      "Gil",
      "Ramírez",
      "Serrano",
      "Blanco",
      "Molina",
      "Morales",
      "Suárez",
    ],
    cities: [
      "Madrid",
      "Barcelona",
      "Valencia",
      "Sevilla",
      "Zaragoza",
      "Málaga",
      "Murcia",
      "Palma",
      "Las Palmas",
      "Bilbao",
      "Alicante",
      "Córdoba",
      "Valladolid",
      "Vigo",
      "Gijón",
      "Granada",
    ],
    streets: [
      "Calle Mayor",
      "Calle Real",
      "Avenida de la Constitución",
      "Calle del Sol",
      "Plaza de España",
      "Calle Nueva",
      "Paseo de Gracia",
      "Calle de Alcalá",
      "Calle San Juan",
      "Avenida de Andalucía",
      "Calle Cervantes",
      "Calle Goya",
      "Ronda de Toledo",
      "Calle La Rosa",
      "Avenida del Puerto",
      "Calle Santa María",
    ],
    addressFormat: "{street}, {number}",
    postcodes: ["#####"],
    regions: [
      "Andalucía",
      "Aragón",
      "Asturias",
      "Islas Baleares",
      "Canarias",
      "Cantabria",
      "Castilla-La Mancha",
      "Castilla y León",
      "Cataluña",
      "Extremadura",
      "Galicia",
      "La Rioja",
      "Madrid",
      "Murcia",
      "Navarra",
      "País Vasco",
    ],
    regionLabel: "Comunidad",
    country: "España",
    countryCode: "ES",
    // Spain keeps no range for fiction, and its plan leaves a first digit of 3 pending attribution to any service.
    phones: ["+34 3## ## ## ##", "+34 3## ### ###"],
    currency: "EUR",
    iban: "ES",
    companySuffixes: ["S.L.", "S.A.", "S.L.U.", "S.Coop.", "S.C."],
  },
  "it-IT": {
    id: "it-IT",
    label: "Italy",
    givenNames: [
      "Leonardo",
      "Sofia",
      "Francesco",
      "Giulia",
      "Alessandro",
      "Aurora",
      "Lorenzo",
      "Ginevra",
      "Mattia",
      "Alice",
      "Tommaso",
      "Emma",
      "Gabriele",
      "Giorgia",
      "Riccardo",
      "Greta",
      "Edoardo",
      "Beatrice",
      "Andrea",
      "Anna",
      "Matteo",
      "Chiara",
      "Diego",
      "Martina",
      "Nicolò",
      "Vittoria",
      "Federico",
      "Elena",
      "Antonio",
      "Sara",
      "Giuseppe",
      "Noemi",
    ],
    surnames: [
      "Rossi",
      "Russo",
      "Ferrari",
      "Esposito",
      "Bianchi",
      "Romano",
      "Colombo",
      "Ricci",
      "Marino",
      "Greco",
      "Bruno",
      "Gallo",
      "Conti",
      "De Luca",
      "Costa",
      "Giordano",
      "Mancini",
      "Rizzo",
      "Lombardi",
      "Moretti",
      "Barbieri",
      "Fontana",
      "Santoro",
      "Mariani",
      "Rinaldi",
      "Caruso",
      "Ferrara",
      "Galli",
      "Martini",
      "Leone",
      "Longo",
      "Gentile",
    ],
    cities: [
      "Roma",
      "Milano",
      "Napoli",
      "Torino",
      "Palermo",
      "Genova",
      "Bologna",
      "Firenze",
      "Bari",
      "Catania",
      "Venezia",
      "Verona",
      "Messina",
      "Padova",
      "Trieste",
      "Brescia",
    ],
    streets: [
      "Via Roma",
      "Via Garibaldi",
      "Via Dante",
      "Corso Vittorio Emanuele",
      "Via Mazzini",
      "Piazza del Duomo",
      "Via Verdi",
      "Via Marconi",
      "Viale Europa",
      "Via San Marco",
      "Via Cavour",
      "Via Manzoni",
      "Piazza Garibaldi",
      "Via Nazionale",
      "Via della Libertà",
      "Corso Italia",
    ],
    addressFormat: "{street} {number}",
    postcodes: ["#####"],
    regions: [
      "Abruzzo",
      "Basilicata",
      "Calabria",
      "Campania",
      "Emilia-Romagna",
      "Lazio",
      "Liguria",
      "Lombardia",
      "Marche",
      "Piemonte",
      "Puglia",
      "Sardegna",
      "Sicilia",
      "Toscana",
      "Umbria",
      "Veneto",
    ],
    regionLabel: "Regione",
    country: "Italia",
    countryCode: "IT",
    // AGCOM keeps no range for fiction, and its plan reserves numbers beginning 2, 6 or 9 for future needs.
    phones: ["+39 2## ### ####", "+39 6## ### ####", "+39 9## ### ####"],
    currency: "EUR",
    iban: "IT",
    companySuffixes: ["S.r.l.", "S.p.A.", "S.n.c.", "S.a.s.", "S.r.l.s."],
  },
  "ja-JP": {
    id: "ja-JP",
    label: "Japan",
    givenNames: [
      "陽翔",
      "結愛",
      "蓮",
      "陽菜",
      "大翔",
      "凛",
      "悠真",
      "芽依",
      "湊",
      "結菜",
      "樹",
      "咲良",
      "律",
      "澪",
      "颯真",
      "莉子",
      "陽向",
      "心春",
      "悠人",
      "葵",
      "朝陽",
      "杏",
      "新",
      "楓",
      "蒼",
      "花",
      "碧",
      "紬",
      "陸",
      "詩",
      "海斗",
      "桜",
    ],
    surnames: [
      "佐藤",
      "鈴木",
      "高橋",
      "田中",
      "伊藤",
      "渡辺",
      "山本",
      "中村",
      "小林",
      "加藤",
      "吉田",
      "山田",
      "佐々木",
      "山口",
      "松本",
      "井上",
      "木村",
      "林",
      "斎藤",
      "清水",
      "山崎",
      "森",
      "池田",
      "橋本",
      "阿部",
      "石川",
      "山下",
      "中島",
      "石井",
      "小川",
      "前田",
      "岡田",
    ],
    familyNameFirst: true,
    cities: [
      "東京",
      "横浜",
      "大阪",
      "名古屋",
      "札幌",
      "福岡",
      "神戸",
      "京都",
      "川崎",
      "さいたま",
      "広島",
      "仙台",
      "千葉",
      "北九州",
      "堺",
      "新潟",
    ],
    // A Japanese address names a district and then a block, not a street, so the list is of districts and the format
    // below is the chome-banchi that follows it.
    streets: [
      "中央",
      "本町",
      "大手町",
      "緑ヶ丘",
      "桜台",
      "若葉",
      "泉",
      "旭町",
      "栄町",
      "春日",
      "新町",
      "東雲",
      "港南",
      "白金",
      "目黒",
      "神楽坂",
    ],
    addressFormat: "{street}{block}",
    postcodes: ["###-####"],
    regions: [
      "北海道",
      "宮城県",
      "東京都",
      "神奈川県",
      "埼玉県",
      "千葉県",
      "愛知県",
      "静岡県",
      "京都府",
      "大阪府",
      "兵庫県",
      "広島県",
      "福岡県",
      "沖縄県",
      "新潟県",
      "長野県",
    ],
    regionLabel: "都道府県",
    country: "日本",
    countryCode: "JP",
    // Japan keeps no range for fiction; no local exchange begins with 0, and the plan gives 030 and 040 no service.
    phones: ["+81 3-0###-####", "+81 30-####-####", "+81 40-####-####"],
    currency: "JPY",
    companySuffixes: ["株式会社", "有限会社", "合同会社"],
  },
};

export const LOCALE_OPTIONS = Object.values(LOCALES).map(({ id, label }) => ({ value: id, label }));

export function isLocale(value: string | null | undefined): value is LocaleId {
  return value !== null && value !== undefined && value in LOCALES;
}
